<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
	currentPage: {
		type: String,
		default: 'curriculum',
	},
	currentArticle: {
		type: Object,
		required: true,
	},
	selectedGame: { type: Object, default: null },
	isGameActive: Boolean,
	user: {
		type: Object,
		required: true,
	},
	windowWidth: {
		type: Number,
		required: true,
	},
	isGlobalNavOpen: {
		type: Boolean,
		required: true,
	},
	isSidebarVisible: {
		type: Boolean,
		required: true,
	},
	toggleModals: {
		type: Function,
		required: true,
	},
	toggleNav: {
		type: Function,
		required: true,
	},
	toggleSidebar: {
		type: Function,
		required: true,
	},
	handleArticleAction: {
		type: Function,
		required: true,
	},
});
const showOpenButton = computed(() => {
	return (
		props.windowWidth < 768 && !props.isSidebarVisible && (
			((props.currentPage === "curriculum" || props.currentPage === "saved") && props.currentArticle.id !== "a0") ||
			((props.currentPage === "games" || props.isGameActive) && props.selectedGame && !props.selectedGame.overview)
		)
	);
});
const openButtonHref = computed(() => (props.currentPage === 'games' || props.isGameActive) ? props.selectedGame?.url : props.currentArticle?.url);
const openButtonLabel = computed(() => {
	if (props.currentPage === 'games' || props.isGameActive) return 'Play Game';
	return props.windowWidth < 410 ? 'Open' : 'Open Article';
});
function handleToggleClick() {
	// close all modals
	props.toggleModals();

	if (props.windowWidth >= 1024 && !['curriculum', 'saved', 'games'].includes(props.currentPage)) { return; }
	props.toggleNav();
}
</script>

<template>
<div id="global-nav" :data-open="isGlobalNavOpen">
	<div id="global-nav-bar" :class="{tablet: windowWidth < 1024 && windowWidth >= 768}">
		<a
			id="nav-toggle"
			:class="['btn toggle icon', {active: isGlobalNavOpen}, {hide: windowWidth >= 1024 && !['curriculum', 'saved', 'games'].includes(currentPage)}]"
			@click="handleToggleClick()"
		>
			<span :class="'fa-solid fa-fw '.concat(isGlobalNavOpen ? 'fa-arrow-up-to-line' : 'fa-bars')"
				:aria-label="'menu '.concat(isGlobalNavOpen ? 'open' : 'close')"></span>
		</a>
		<div
			class="button-bar"
			v-if="(['curriculum', 'saved', 'games'].includes(currentPage)) && windowWidth < 768"
		>
			<a
				:class="['btn toggle', isSidebarVisible ? 'active' : '']"
				@click.stop="toggleSidebar('open')"
			>
				<span>List</span>
			</a>
			<a
				:class="['btn toggle', isSidebarVisible ? '' : 'active']"
				@click.stop="toggleSidebar('closed')"
			>
				<span>Summary</span>
			</a>
		</div>
		<h3 v-else id="logo">The <b>Design Externship</b></h3>

		<a
			v-if="showOpenButton"
			id="open"
			class="btn open-article primary"
			:href="openButtonHref"
			target="_blank"
			@click.stop="currentPage === 'games' || isGameActive ? null : handleArticleAction('read', true)"
		>
			<span>{{ openButtonLabel }}</span>
			<i v-if="windowWidth >= 350" :class="currentPage === 'games' || isGameActive ? 'fa-gamepad fa-solid' : 'fa-browsers fa-solid'"></i>
		</a>

		<a id="toggle-sidebar"
			v-if="(['curriculum'].includes(currentPage)) && windowWidth >= 768 && windowWidth < 1024"
			:class="['btn toggle icon', isSidebarVisible ? '' : 'active']"
			:title="isSidebarVisible ? 'Hide List' : 'Show List'"
			@click="toggleSidebar()"
		>
			<i class="fa-solid fa-sidebar" aria-label="Toggle List"></i>
		</a>
	</div>
	<slot name="global-nav-drawer"></slot>
	<slot name="clear-filters"></slot>
</div>
</template>

<style lang="scss" scoped>
#global-nav-bar {
	background-color: var(--bg3);
	background-color: var(--bg3a9);
	border: 1px solid var(--border);
	border-radius: 18px; /* match article reactions*/
	box-shadow: var(--floatingShadow);
	backdrop-filter: blur(3px) brightness(1.2);

	display: flex;
	flex-flow: row;
	align-items: center;
	justify-content: flex-start;
	gap: 4px;
	padding: 4px;

	position: fixed;
	inset: auto calc(var(--nav-padding) - 6px) 8px; /* implicit nav height is 52, and is manually written to --global-nav-height in the root vars on global.css */
	z-index:10;
}
#logo {
	display: inline-block;
	font-weight: 400;
	line-height: 1.15em;
	margin: 0 0 0 12px;

	b {font-weight: 700;}
}
#global-nav-bar #toggle-sidebar {display:none;}
.open-article {
	border: 1px solid var(--border);
	flex-grow: 2;
	margin-left: auto;
	max-width: 240px;
}
@media(min-width:768px){
	#global-nav-bar.tablet {
		inset: auto auto 8px calc(var(--nav-padding) - 6px);
		width: calc(var(--sidebar-width) - 32px - 1px);
	}
	#global-nav-bar .button-bar {display:none;}
	#global-nav-bar #toggle-sidebar {
		display:inline-flex;
		margin-left:auto;
	}
}
@media(min-width:1024px){
	#global-nav {
		display: flex;
		flex-flow: column;
		flex-grow: 2;
	}
	#global-nav-bar,
	#global-nav-bar.tablet {
		background-color:var(--bg1);
		border: unset;
		border-radius: unset;
		box-shadow: unset;
		position: static;
		flex-flow: row-reverse;
		justify-content: space-between;
		inset: unset;
		width: -webkit-fill-available;
	}

	#global-nav-bar[data-open="true"] + #curriculum { padding-top: .01px; }

	#nav-toggle {
	  border-color: transparent;
  	padding: 14px 12px;
  	&.hide {
  		opacity: 0;
  		cursor: default;
  	}
	}
}

#global-nav-bar * {
	user-select: none;
	-webkit-user-select: none;
}
</style>
